import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, type Profile, type Release } from "@/lib/api";
import { formatReleaseDate, REPOS, statusVariant, worstAssetStatus } from "@/lib/releases";

const RELEASES_PER_PAGE = 5;

function ProfileRepoTable({
  profile,
  repo,
  releases,
  page,
  onPageChange,
  onToggleAssigned,
}: {
  profile: Profile;
  repo: string;
  releases: Release[];
  page: number;
  onPageChange: (page: number) => void;
  onToggleAssigned: (releaseId: string, assigned: boolean) => void;
}) {
  if (releases.length === 0) {
    return (
      <div className="space-y-1">
        <h3 className="text-xs font-semibold text-muted-foreground">{repo}</h3>
        <p className="text-sm text-muted-foreground">No releases synced yet.</p>
      </div>
    );
  }

  const totalPages = Math.ceil(releases.length / RELEASES_PER_PAGE);
  const currentPage = Math.min(page, totalPages);
  const visible = releases.slice(
    (currentPage - 1) * RELEASES_PER_PAGE,
    currentPage * RELEASES_PER_PAGE,
  );

  return (
    <div className="space-y-1">
      <h3 className="text-xs font-semibold text-muted-foreground">{repo}</h3>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10" />
            <TableHead>Tag</TableHead>
            <TableHead>Published</TableHead>
            <TableHead>Assets</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {visible.map((release) => {
            const assigned = profile.release_ids.includes(release.id);
            return (
              <TableRow key={release.id}>
                <TableCell>
                  <input
                    type="checkbox"
                    aria-label={`Assign ${release.tag_name} to ${profile.name}`}
                    checked={assigned}
                    onChange={(e) => onToggleAssigned(release.id, e.target.checked)}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span>{release.tag_name}</span>
                    {!release.enabled && <Badge variant="secondary">disabled</Badge>}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatReleaseDate(release.published_at)}
                </TableCell>
                <TableCell>
                  <Badge variant={statusVariant(worstAssetStatus(release.assets))}>
                    {worstAssetStatus(release.assets)}
                  </Badge>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {totalPages > 1 && (
        <Pagination className="justify-start">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  if (currentPage > 1) onPageChange(currentPage - 1);
                }}
                className={currentPage === 1 ? "pointer-events-none opacity-50" : undefined}
              />
            </PaginationItem>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
              <PaginationItem key={pageNumber}>
                <PaginationLink
                  href="#"
                  size="icon"
                  isActive={pageNumber === currentPage}
                  onClick={(e) => {
                    e.preventDefault();
                    onPageChange(pageNumber);
                  }}
                >
                  {pageNumber}
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNext
                href="#"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  if (currentPage < totalPages) onPageChange(currentPage + 1);
                }}
                className={
                  currentPage === totalPages ? "pointer-events-none opacity-50" : undefined
                }
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}

export function Profiles() {
  const queryClient = useQueryClient();
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [repoPages, setRepoPages] = useState<Record<string, number>>({});
  const [collapsedProfiles, setCollapsedProfiles] = useState<Set<string>>(new Set());

  const { data: profiles, isLoading } = useQuery({
    queryKey: ["profiles"],
    queryFn: api.listProfiles,
  });
  const { data: releases } = useQuery({
    queryKey: ["releases"],
    queryFn: api.listReleases,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["profiles"] });
    void queryClient.invalidateQueries({ queryKey: ["releases"] });
  };

  const createProfile = useMutation({
    mutationFn: () => api.createProfile(slug.trim(), name.trim()),
    onSuccess: () => {
      setSlug("");
      setName("");
      setError(null);
      invalidate();
    },
    onError: () => setError("Could not create profile — slug may already exist"),
  });

  const deleteProfile = useMutation({
    mutationFn: api.deleteProfile,
    onSuccess: invalidate,
  });

  const assignRelease = useMutation({
    mutationFn: ({ profileId, releaseId }: { profileId: string; releaseId: string }) =>
      api.assignReleaseToProfile(profileId, releaseId),
    onSuccess: invalidate,
  });

  const unassignRelease = useMutation({
    mutationFn: ({ profileId, releaseId }: { profileId: string; releaseId: string }) =>
      api.unassignReleaseFromProfile(profileId, releaseId),
    onSuccess: invalidate,
  });

  const handleCreate = (event: FormEvent) => {
    event.preventDefault();
    if (!slug.trim() || !name.trim()) return;
    createProfile.mutate();
  };

  const setRepoPage = (key: string, page: number) => {
    setRepoPages((prev) => ({ ...prev, [key]: page }));
  };

  const toggleCollapsed = (profileId: string) => {
    setCollapsedProfiles((prev) => {
      const next = new Set(prev);
      if (next.has(profileId)) next.delete(profileId);
      else next.add(profileId);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold">Profiles</h1>
        <p className="max-w-2xl text-sm text-muted-foreground">
          A profile is a release channel a machine is assigned to. Assign a release to the
          "dev" profile to roll it out early, then assign it to "prod" once it's proven out.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Add profile</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreate} className="flex items-end gap-2">
            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                className="w-40"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="dev"
              />
            </div>
            <div className="flex-1 space-y-2">
              <Label htmlFor="profile-name">Name</Label>
              <Input
                id="profile-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Development"
              />
            </div>
            <Button type="submit" disabled={createProfile.isPending}>
              Add profile
            </Button>
          </form>
          {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
        </CardContent>
      </Card>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {profiles?.length === 0 && <p className="text-sm text-muted-foreground">No profiles yet.</p>}

      <div className="space-y-4">
        {profiles?.map((profile) => {
          const collapsed = collapsedProfiles.has(profile.id);
          return (
            <Card key={profile.id}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <button
                  type="button"
                  className="flex items-center gap-2"
                  onClick={() => toggleCollapsed(profile.id)}
                  aria-expanded={!collapsed}
                >
                  {collapsed ? (
                    <ChevronRight className="size-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="size-4 text-muted-foreground" />
                  )}
                  <CardTitle className="text-base font-medium">{profile.name}</CardTitle>
                  <Badge variant="secondary">{profile.slug}</Badge>
                  <span className="text-xs text-muted-foreground">
                    {profile.release_ids.length} assigned
                  </span>
                </button>
                <Button
                  variant="link"
                  className="h-auto p-0 text-destructive"
                  onClick={() => deleteProfile.mutate(profile.id)}
                >
                  Delete
                </Button>
              </CardHeader>
              {!collapsed && (
                <CardContent className="space-y-4">
                  {releases?.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No releases synced yet.</p>
                  ) : (
                    REPOS.map((repo) => {
                      const repoReleases =
                        releases?.filter((release) => release.repo === repo) ?? [];
                      const key = `${profile.id}:${repo}`;
                      return (
                        <ProfileRepoTable
                          key={repo}
                          profile={profile}
                          repo={repo}
                          releases={repoReleases}
                          page={repoPages[key] ?? 1}
                          onPageChange={(page) => setRepoPage(key, page)}
                          onToggleAssigned={(releaseId, assigned) =>
                            assigned
                              ? assignRelease.mutate({ profileId: profile.id, releaseId })
                              : unassignRelease.mutate({ profileId: profile.id, releaseId })
                          }
                        />
                      );
                    })
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
